import { useCallback, useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useMe } from "@/context/MeContext";
import { errMsg, eur, fmtDate, useApi } from "@/lib/api";
import { StateBadge } from "@/components/club/StatusBadge";
import { OfferForm } from "@/components/partner/OfferForm";
import { CsvImport } from "@/components/partner/CsvImport";
import { PartnerForm } from "@/components/partner/PartnerForm";

function Catalog({ offers, api, reload }) {
  const [edit, setEdit] = useState(null);
  const update = async (id, data, msg) => {
    try {
      await api("put", `/partner/offers/${id}`, data);
      toast.success(msg);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!offers.length) return <p className="text-slate-500">Nessuna offerta nel catalogo.</p>;
  return (
    <div className="overflow-x-auto rounded-2xl border border-white/10" data-testid="partner-catalog">
      <table className="w-full text-left text-sm">
        <thead className="bg-white/5 text-xs uppercase text-slate-400"><tr><th className="p-3">Offerta</th><th className="p-3">Prezzi B / G / D</th><th className="p-3">Stock</th><th className="p-3">Stato</th><th className="p-3">Visibile</th><th className="p-3" /></tr></thead>
        <tbody>
          {offers.map((o) => (
            <tr key={o.id} className="border-t border-white/5" data-testid={`partner-offer-row-${o.id}`}>
              <td className="p-3"><p className="text-white">{o.name}</p><p className="text-xs text-slate-500">{o.category}</p></td>
              <td className="p-3 whitespace-nowrap">{eur(o.price_bronze)} / {eur(o.price_gold)} / {eur(o.price_diamond)}</td>
              <td className="p-3">
                <input type="number" min="0" defaultValue={o.stock} data-testid={`partner-stock-${o.id}`} className="w-20 rounded-lg border border-white/10 bg-[#1C1F2C] px-2 py-1"
                  onBlur={(e) => Number(e.target.value) !== o.stock && update(o.id, { stock: Number(e.target.value) }, "Stock aggiornato")} />
              </td>
              <td className="p-3"><StateBadge status={o.status} />{o.note && <p className="mt-1 text-xs text-red-300">{o.note}</p>}</td>
              <td className="p-3"><Switch data-testid={`partner-visible-${o.id}`} checked={o.visible} onCheckedChange={(v) => update(o.id, { visible: v }, v ? "Offerta mostrata" : "Offerta nascosta")} /></td>
              <td className="p-3"><button data-testid={`partner-edit-${o.id}`} onClick={() => setEdit(o)} className="text-amber-400 hover:underline">Modifica</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      {edit && (
        <Dialog open onOpenChange={(v) => !v && setEdit(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#12141C] sm:max-w-2xl">
            <DialogHeader><DialogTitle className="text-white">Modifica offerta</DialogTitle></DialogHeader>
            <p className="text-xs text-slate-500">Le modifiche a prezzi, testi o immagine tornano in approvazione.</p>
            <OfferForm initial={edit} submitLabel="Salva modifiche" onSubmit={async (d) => { await update(edit.id, d, "Offerta aggiornata"); setEdit(null); }} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function PartnerOrders({ orders, api, reload }) {
  const setStatus = async (id, status) => {
    try {
      await api("post", `/partner/orders/${id}/status`, { status });
      toast.success("Stato aggiornato");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!orders.length) return <p className="text-slate-500">Nessun ordine pagato.</p>;
  return (
    <div className="space-y-3">
      {orders.map((o) => (
        <div key={o.id} className="rounded-2xl border border-white/10 bg-[#12141C] p-5" data-testid={`partner-order-${o.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-semibold text-white">{o.causale} · {o.customer_name}</p>
            <StateBadge status={o.status} />
          </div>
          <p className="mt-1 text-sm text-slate-400">{o.items.map((i) => `${i.qty} × ${i.name}`).join(", ")} · {eur(o.total)} · pagato il {fmtDate(o.paid_at)}</p>
          <p className="mt-1 text-sm text-slate-400">
            {o.delivery === "pickup" ? "Ritiro in sede" : `Spedire a: ${o.address?.full_name}, ${o.address?.street}, ${o.address?.zip} ${o.address?.city} ${o.address?.province}`} · Tel. {o.phone}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {[["in_preparazione", "In preparazione"], [o.delivery === "pickup" ? "pronto_ritiro" : "spedito", o.delivery === "pickup" ? "Pronto per il ritiro" : "Spedito"]].map(([s, l]) => (
              <button key={s} data-testid={`partner-order-${s}-${o.id}`} disabled={o.status === s || (s === "in_preparazione" && ["spedito", "pronto_ritiro", "consegnato"].includes(o.status))} onClick={() => setStatus(o.id, s)} className={`rounded-full border px-3 py-1 text-xs transition-colors disabled:cursor-not-allowed ${o.status === s ? "border-amber-500 bg-amber-500/15 text-amber-200" : "border-white/15 text-white hover:bg-white/10 disabled:opacity-40"}`}>{l}</button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Partner() {
  const { me, refresh } = useMe();
  const api = useApi();
  const [partner, setPartner] = useState(undefined);
  const [offers, setOffers] = useState([]);
  const [orders, setOrders] = useState([]);

  const load = useCallback(async () => {
    const r = await api("get", "/partner/me");
    setPartner(r.partner);
    if (r.partner?.status === "approved") {
      const [o, ord] = await Promise.all([api("get", "/partner/offers"), api("get", "/partner/orders")]);
      setOffers(o);
      setOrders(ord);
    }
  }, [api]);

  useEffect(() => { load().catch(() => setPartner(null)); }, [load]);

  if (partner === undefined) return <p className="py-24 text-center text-slate-500">Caricamento…</p>;

  if (!partner)
    return (
      <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6" data-testid="partner-apply-page">
        <p className="font-club text-xs text-amber-400">DIVENTA PARTNER</p>
        <h1 className="mt-2 font-display text-4xl font-extrabold text-white">Vendi ai membri del club</h1>
        <p className="mt-3 text-slate-400">Compila la richiesta di adesione: l'Admin la verificherà e attiverà la tua area aziendale.</p>
        <div className="mt-8 rounded-3xl border border-white/10 bg-[#12141C] p-6">
          <PartnerForm initial={{ company_name: "", vat_number: "", contact_email: me?.email || "", phone: "", address: "", iban: "", shipping_cost: 0, pickup: false }} submitLabel="Invia richiesta"
            onSubmit={async (d) => { try { await api("post", "/partner/apply", d); toast.success("Richiesta inviata!"); load(); } catch (e) { toast.error(errMsg(e)); } }} />
        </div>
      </div>
    );

  if (partner.status !== "approved")
    return (
      <div className="mx-auto max-w-xl px-4 py-24 text-center" data-testid="partner-pending">
        <Clock className="mx-auto h-10 w-10 text-amber-400" />
        <h1 className="mt-4 font-display text-3xl font-bold text-white">{partner.status === "pending" ? "Richiesta in attesa di approvazione" : partner.status === "suspended" ? "Account partner sospeso" : "Richiesta non approvata"}</h1>
        {partner.note && <p className="mt-3 text-slate-400">Nota dell'Admin: {partner.note}</p>}
      </div>
    );

  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6" data-testid="partner-dashboard">
      <p className="font-club text-xs text-amber-400">AREA PARTNER</p>
      <h1 className="mt-2 font-display text-4xl font-extrabold text-white">{partner.company_name}</h1>
      <Tabs defaultValue="catalog" className="mt-8">
        <TabsList className="flex h-auto flex-wrap bg-[#12141C]">
          <TabsTrigger value="catalog" data-testid="partner-tab-catalog">Gestione catalogo</TabsTrigger>
          <TabsTrigger value="new" data-testid="partner-tab-new">Nuova offerta</TabsTrigger>
          <TabsTrigger value="csv" data-testid="partner-tab-csv">Importa listino (CSV)</TabsTrigger>
          <TabsTrigger value="orders" data-testid="partner-tab-orders">Ordini ({orders.length})</TabsTrigger>
          <TabsTrigger value="profile" data-testid="partner-tab-profile">Dati azienda</TabsTrigger>
        </TabsList>
        <TabsContent value="catalog" className="mt-6"><Catalog offers={offers} api={api} reload={load} /></TabsContent>
        <TabsContent value="new" className="mt-6 max-w-3xl">
          <OfferForm onSubmit={async (d) => { try { await api("post", "/partner/offers", d); toast.success("Offerta inviata per approvazione"); load(); } catch (e) { toast.error(errMsg(e)); } }} />
        </TabsContent>
        <TabsContent value="csv" className="mt-6"><CsvImport onDone={load} /></TabsContent>
        <TabsContent value="orders" className="mt-6"><PartnerOrders orders={orders} api={api} reload={load} /></TabsContent>
        <TabsContent value="profile" className="mt-6 max-w-3xl">
          <PartnerForm initial={partner} submitLabel="Salva dati" onSubmit={async (d) => { try { await api("put", "/partner/profile", d); toast.success("Dati aggiornati"); load(); refresh(); } catch (e) { toast.error(errMsg(e)); } }} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
